import { useRef, useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  CardContent,
  Box,
  Grid,
  Typography,
  Switch,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  useTheme,
} from "@material-ui/core";
import * as Yup from "yup";
import { Field } from "formik";
import AlertMessage from "src/components/ui-components/AlertMessage";
import FormikForm, {
  FormikStep,
} from "src/components/ui-components/FormikForm";
import {
  parsePhoneNumber,
  isPossiblePhoneNumber,
} from "react-phone-number-input";
import { IUser, IUserGroups } from "src/models/account/accountModel";
import PhoneInput from "src/components/shared/PhoneInput";
// import Header from 'src/pages/common/header';
import InputField from "src/components/ui-components/InputField";
import SelectField from "src/components/ui-components/SelectField";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import {
  getConfigUsersList,
  getConfigUsersById,
  addUpdateConfigUser,
  getConfigUserGroups,
} from "../../../redux/account/connectionServices";
import ChevronRightIcon from "src/icons/ChevronRight";

interface ParamsType {
  userId: Number;
}

const defaultForm = {
  configUserKey: null,
  fullName: "",
  email: "",
  mobile: "",
  configUserGroupKey: "",
  isDisabled: true,
  isLocked: true,
  username: "",
  password: "",
  isEdit: false,
};
const UserCreateEdit = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [usersList, setUsersList] = useState<IUser[]>([]);
  const { userId } = (location.state as ParamsType) || { userId: null };
  const formRef = useRef(null);
  const [userGroupList, setUserGroupList] = useState<IUserGroups[]>([]);
  const userEmail = JSON.parse(localStorage.getItem("userDetails"))?.email;
  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: userId && true },
      });
    }
  };
  const submitForm = (e) => {
    if (formRef && formRef.current && !formRef.current.isSubmitting) {
      formRef.current.submitForm();
    }
    e.preventDefault();
  };
  const fetchUserGroups = async (userEmail) => {
    setIsFetching(true);
    await getConfigUserGroups(userEmail)
      .then((res) => {
        setUserGroupList(res);
        setIsFetching(false);
      })
      .catch(() => {
        setIsFetching(false);
      });
  };
  const fetchUsersList = async (userEmail) => {
    await getConfigUsersList(userEmail)
      .then((res) => {
        setUsersList(res);
      })
      .catch((err) => {
        AlertMessage({ type: "error", msg: err.title });
      });
  };
  const fetchUserGroupById = async () => {
    setIsFetching(true);
    await getConfigUsersById(userId)
      .then((res) => {
        resetFormData(res);
        setIsFetching(false);
      })
      .catch((err) => {
        setIsFetching(false);
        AlertMessage({ type: "error", msg: err.title });
      });
  };
  useEffect(() => {
    fetchUsersList(userEmail);
    fetchUserGroups(userEmail);
    if (userId) {
      fetchUserGroupById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              alignItems="center"
              sx={{ my: 2 }}
              container
              justifyContent="space-between"
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Administration
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Users
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid alignItems="center" container spacing={2}>
                  <Grid item>
                    <Button
                      component={RouterLink}
                      to="/users"
                      sx={{
                        background:
                          "linear-gradient(to bottom, #939ca3, #939ca3)",
                        minWidth: 100,
                      }}
                      // sx={{ background: '#808080', // Gray color, you can adjust the color value
                      //     minWidth: 100 }}
                      variant="contained"
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      sx={{
                        background:
                          "linear-gradient(to bottom, #072f40, #072f40)",
                        minWidth: 100,
                      }}
                      component={RouterLink}
                      to=""
                      onClick={submitForm}
                      variant="contained"
                    >
                      Save
                      {isLoading && (
                        <CircularProgress
                          color="inherit"
                          thickness={5}
                          size={20}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <CardContent>
              {isFetching && <CustomSkeleton skeletonType="FormEdit" />}
              <Box sx={{ display: isFetching ? "none" : "" }}>
                <FormikForm
                  innerRef={formRef}
                  initialValues={defaultForm}
                  onSubmit={async (values): Promise<void> => {
                    setIsLoading(true);
                    await addUpdateConfigUser(values as IUser)
                      .then(() => {
                        setIsLoading(false);
                        const msg = values.isEdit
                          ? "User details updated successfully"
                          : "User details inserted successfully";
                        AlertMessage({ type: "success", msg });
                        navigate("/users");
                      })
                      .catch((err) => {
                        setIsLoading(false);
                        if (err && err.errors) {
                          Object.entries(err.errors).forEach(
                            ([, errMsg]: any) => {
                              AlertMessage({
                                type: "error",
                                msg: errMsg.errorMessage,
                              });
                            }
                          );
                        } else {
                          AlertMessage({ type: "error", msg: err.title });
                        }
                        return false;
                      });
                  }}
                >
                  <FormikStep
                    validationSchema={Yup.object({
                      fullName: Yup.string()
                        .max(100)
                        .required("Full name is required"),
                      // .test('fullNameValidation', 'Full name is required',
                      //     (value, { createError, parent }) => {
                      //         if (usersList.some((x) => x.fullName?.toLocaleLowerCase() === value?.toLocaleLowerCase() && x.userKey !== parent.userKey)) {
                      //             return createError({ message: 'Full name already exists' });
                      //         }
                      //         return true;
                      //     }),
                      username: Yup.string()
                        .max(100)
                        .required("User name is required")
                        .test(
                          "usernameValidation",
                          "User name is required",
                          (value, { createError, parent }) => {
                            if (
                              usersList.some(
                                (x) =>
                                  x.username?.toLocaleLowerCase() ===
                                    value?.toLocaleLowerCase() &&
                                  x.configUserKey !== parent.configUserKey
                              )
                            ) {
                              return createError({
                                message: "User name already exists",
                              });
                            }
                            return true;
                          }
                        ),
                      configUserGroupKey: Yup.string()
                        .nullable()
                        .required("User role is required"),
                      email: Yup.string()
                        .email("Must be a valid email")
                        .max(50)
                        // .test('emailValidation', 'Email is required',
                        //     (value, { createError, parent }) => {
                        //         if (usersList.some((x) => x.email?.toLocaleLowerCase() === value?.toLocaleLowerCase() && x.userKey !== parent.userKey)) {
                        //             return createError({ message: 'Email Address already exists' });
                        //         }
                        //         return true;
                        //     })
                        .required("email is required"),
                      mobile: Yup.string()
                        .nullable()
                        .required("Phone number is required")
                        .test(
                          "phoneValidation",
                          "Must be a valid phone number",
                          (value) => isPossiblePhoneNumber(value || "")
                        ),
                      // configUserKey: Yup
                      //     .string()
                      //     .nullable(),
                      password: Yup.string()
                        .max(20)
                        .when("userKey", {
                          is: null,
                          then: Yup.string()
                            .required("Password is required")
                            .test(
                              "regex",
                              "Password must be min 8 characters, and have 1 Special Character, 1 Uppercase, 1 Number and 1 Lowercase",
                              (val) => {
                                const regExp = new RegExp(
                                  "^(?=.*\\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$"
                                );
                                return regExp.test(val);
                              }
                            ),
                        }),
                    })}
                  >
                    <Grid spacing={2} container>
                      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                        <InputField label="Full Name" name="fullName" />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                        <InputField
                          label="Email Address"
                          name="email"
                          type="email"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                        <InputField
                          disabled={userId && true}
                          label="User Name"
                          name="username"
                        />
                      </Grid>
                      {!userId && (
                        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                          <InputField
                            label="Password"
                            name="password"
                            type="password"
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                        <Field name="mobile">
                          {({
                            field,
                            form: {
                              errors,
                              handleBlur,
                              values,
                              setErrors,
                              touched,
                            },
                            meta,
                          }) => (
                            <PhoneInput
                              label="Mobile"
                              id="phoneInput"
                              name="mobile"
                              {...field}
                              onBlur={(e) => {
                                setPhoneTouched(true);
                                handleBlur(e);
                              }}
                              value={values.mobile}
                              error={touched.mobile && Boolean(meta.error)}
                              helperText={touched.mobile && meta.error}
                              onChange={(mob: string): any => {
                                values.mobile = mob || "";
                                if (isPossiblePhoneNumber(values.mobile)) {
                                  const phoneNumber = parsePhoneNumber(
                                    values.mobile
                                  );
                                  if (phoneNumber) {
                                    setErrors({ ...errors, mobile: undefined });
                                  }
                                } else {
                                  if (phoneTouched) {
                                    meta.touched = true;
                                  }
                                  setErrors({
                                    ...errors,
                                    mobile: "Must be a valid phone number",
                                  });
                                }
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                        <SelectField
                          name="configUserGroupKey"
                          label="User Role"
                          data={userGroupList}
                          dataTextField="configUserGroup"
                          dataValueField="configUserGroupKey"
                        />
                      </Grid>
                      {userId && (
                        <Grid item xs={6}>
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Field name="isLocked">
                                {({
                                  form: { values, handleChange, handleBlur },
                                }) => (
                                  <>
                                    <Typography
                                      color="textPrimary"
                                      gutterBottom
                                      variant="subtitle2"
                                    >
                                      Is Locked
                                    </Typography>
                                    <Switch
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      value={values.isLocked}
                                      checked={values.isLocked}
                                      name="isLocked"
                                      color="primary"
                                      edge="start"
                                    />
                                  </>
                                )}
                              </Field>
                            </Grid>
                            <Grid item xs={4}>
                              <Field name="isDisabled">
                                {({
                                  form: { values, handleChange, handleBlur },
                                }) => (
                                  <>
                                    <Typography
                                      color="textPrimary"
                                      gutterBottom
                                      variant="subtitle2"
                                    >
                                      Is Account Disabled
                                    </Typography>
                                    <Switch
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      value={values.isDisabled}
                                      checked={values.isDisabled}
                                      name="isDisabled"
                                      color="primary"
                                      edge="start"
                                    />
                                  </>
                                )}
                              </Field>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </FormikStep>
                </FormikForm>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default UserCreateEdit;
